import classnames from 'classnames';
import React from 'react';
import { Icon } from '../../components/Icon/Icon';
import { IconName } from '../../components/Icon/IconName';
import { t } from '../../locales/translate';
import {
  Ticket,
  TicketScannerActivityLogItem,
  TicketScannerActivityLogItemWithTicket,
} from '../../model/ticketScanner';
import { TimeStamp } from './TimeStamp';

import './ActivityLog.css';

interface Props {
  scannerName: string;
  log: TicketScannerActivityLogItemWithTicket[];
  onLogOpen?: () => void;
  offline: boolean;
}

const typeToNameMap: Record<TicketScannerActivityLogItem['type'], string> = {
  invalid: t.page.ticketScanner.invalid,
  already_checked: t.page.ticketScanner.checked,
  valid: t.page.ticketScanner.scan,
};

const typeToIconMap: Record<TicketScannerActivityLogItem['type'], IconName> = {
  invalid: 'invalid',
  already_checked: 'door',
  valid: 'valid',
};

const createSeatInfo = (ticket?: Ticket): string | null => {
  if (!ticket || !ticket.seat) {
    return null;
  }
  const { venueSection, row, name } = ticket.seat;

  const info = {
    [t.page.ticketScanner.section]: venueSection?.name,
    [t.page.ticketScanner.row]: row,
    [t.page.ticketScanner.seat]: name,
  };

  return Object.entries(info)
    .map(([key, val]) => (val ? `${key} ${val}` : null))
    .filter(Boolean)
    .join(', ');
};

const getIconName = (logItem: TicketScannerActivityLogItem): IconName => {
  if (logItem.type === 'already_checked' && !logItem.wasCheckedOut) {
    return 'door-closed';
  }

  return typeToIconMap[logItem.type];
};

const getActivityName = (logItem: TicketScannerActivityLogItem): string => {
  if (logItem.type === 'already_checked' && logItem.wasCheckedOut) {
    return t.page.ticketScanner.checkedOut;
  }

  return logItem.isManual ? t.page.ticketScanner.manualEntry : typeToNameMap[logItem.type];
};
export function ActivityLog({ log = [], offline, scannerName }: Props): JSX.Element {
  const notSyncedLogItemsCount = log.filter((e) => e.needsSync).length;
  return (
    <>
      <div className={classnames('activity-log')}>
        <div className="activity-log__container">
          <div className="activity-log__header">
            <h3 className="activity-log__title">
              {t.page.ticketScanner.activityLog}
              {scannerName !== 'default' && <> - {scannerName}</>}
            </h3>
            <div className="activity-log__sync-indicator">
              {notSyncedLogItemsCount > 0 && <p>{notSyncedLogItemsCount}</p>}
              {offline && <span className="activity-log__sync-indicator-circle"></span>}
            </div>
          </div>

          <ul className="activity-log__activity-list">
            {log.length === 0 ? (
              <li className="activity-log__empty-log">
                <div className="activity-log__empty-log-icon-wrapper">
                  <Icon name="hand-horns" />
                  <p>zZ</p>
                </div>
                <p>{t.page.ticketScanner.noActivity}</p>
              </li>
            ) : (
              log.map((logItem) => {
                const { code, type, scannedAt, ticket } = logItem;

                const seat = createSeatInfo(ticket);
                return (
                  <li key={new Date(scannedAt).getTime()} className="activity-log__activity">
                    <Icon
                      name={getIconName(logItem)}
                      className={classnames(
                        'activity-log__activity-icon',
                        `activity-log__activity-icon--${typeToIconMap[type]}`
                      )}
                    />
                    <p
                      className={classnames('activity-log__activity-header', `activity-log__activity-header--${type}`)}
                    >
                      {getActivityName(logItem)}
                      {ticket?.validatedBy &&
                        ticket.validatedBy !== 'default' &&
                        type === 'already_checked' &&
                        ` - ${ticket.validatedBy}`}
                      <TimeStamp date={scannedAt} />
                    </p>
                    <p className="activity-log__activity-text activity-log__activity-text--bold">
                      {ticket?.personalIdentifier && ` - ${ticket.personalIdentifier}`}
                    </p>
                    <p className="activity-log__activity-text">{code}</p>
                    {seat && <p className="activity-log__activity-text">{seat}</p>}
                  </li>
                );
              })
            )}
          </ul>
        </div>
      </div>
    </>
  );
}
